import { useState } from "react";
import { Button } from "../components/ui/button";
import { Compass, Zap, Shield, ArrowRight, LayoutGrid } from "lucide-react";

const STEPS = [
  {
    title: "Welcome to the Unstoppable Company Game",
    subtitle: "Your strategic AI decision-making tool",
    content: (
      <div className="space-y-6">
        <p className="text-slate-600 leading-relaxed text-base">
          The USG helps you and your leadership team map every workflow in your business and make a strategic decision about AI's role in each one.
        </p>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          {[
            { icon: LayoutGrid, label: "10 Departments", desc: "60 total workflow areas to evaluate" },
            { icon: Zap, label: "4 Decisions", desc: "Delete, Automate, Augment, or Protect each area" },
            { icon: Compass, label: "AI Guidance", desc: "Get intelligent suggestions per workflow" },
          ].map((item, i) => (
            <div key={i} className="bg-white rounded-xl border border-slate-200 p-5 text-center">
              <div className="w-10 h-10 bg-indigo-50 rounded-lg flex items-center justify-center mx-auto mb-3">
                <item.icon className="w-5 h-5 text-indigo-600" />
              </div>
              <div className="font-heading font-semibold text-sm text-slate-900">{item.label}</div>
              <div className="text-xs text-slate-500 mt-1">{item.desc}</div>
            </div>
          ))}
        </div>
      </div>
    ),
  },
  {
    title: "Understanding the Four Decisions",
    subtitle: "Every workflow gets one of these classifications",
    content: (
      <div className="space-y-4">
        {[
          {
            label: "Delete",
            color: "bg-red-50 border-red-200",
            textColor: "text-red-800",
            dotColor: "bg-red-500",
            desc: "Is this adding value or should we not be doing it? Question whether this workflow needs to exist at all before deciding how to improve it.",
            example: "Redundant approvals, Legacy reports nobody reads, Manual data re-entry",
          },
          {
            label: "Automate",
            color: "bg-amber-50 border-amber-200",
            textColor: "text-amber-800",
            dotColor: "bg-amber-500",
            desc: "The workflow is rules-based, repetitive, or data-heavy. AI performs the core task while humans handle exceptions and oversight.",
            example: "Invoice processing, Data entry, Report generation",
          },
          {
            label: "Augment",
            color: "bg-violet-50 border-violet-200",
            textColor: "text-violet-800",
            dotColor: "bg-violet-500",
            desc: "The workflow benefits from human judgment, but AI significantly enhances speed and accuracy. Think 'Iron Man suit' — the human is more capable with AI.",
            example: "Sales forecasting, Content creation, Market research",
          },
          {
            label: "Protect",
            color: "bg-emerald-50 border-emerald-200",
            textColor: "text-emerald-800",
            dotColor: "bg-emerald-500",
            desc: "The workflow's value is fundamentally human. Empathy, ethics, creativity, or trust-based relationships. This is a strategic choice, not a limitation.",
            example: "Culture building, Executive coaching, Strategic vision",
          },
        ].map((item, i) => (
          <div key={i} className={`rounded-xl border p-5 ${item.color}`}>
            <div className="flex items-center gap-2.5 mb-2">
              <div className={`w-3 h-3 rounded-full ${item.dotColor}`} />
              <span className={`font-heading font-bold text-sm ${item.textColor}`}>{item.label}</span>
            </div>
            <p className={`text-sm leading-relaxed ${item.textColor} opacity-80`}>{item.desc}</p>
            <p className={`text-xs mt-2 ${item.textColor} opacity-60`}>Examples: {item.example}</p>
          </div>
        ))}
      </div>
    ),
  },
  {
    title: "How It Works",
    subtitle: "Complete the game in a few simple steps",
    content: (
      <div className="space-y-5">
        {[
          { step: "1", title: "Create a Board", desc: "Start a new game board for your company. Choose your industry for tailored suggestions." },
          { step: "2", title: "Review Each Department", desc: "Go through 10 departments, each with 6 key workflow areas. Toggle off departments that don't apply." },
          { step: "3", title: "Make Decisions", desc: "For each workflow, decide: Automate, Augment, or Protect. Add notes and confidence levels." },
          { step: "4", title: "Get AI Insights", desc: "Use AI-powered suggestions as a starting point. Your context and judgment always come first." },
          { step: "5", title: "Export & Act", desc: "View your analytics summary and export decisions as use cases to drive your AI roadmap." },
        ].map((item, i) => (
          <div key={i} className="flex items-start gap-4">
            <div className="w-8 h-8 bg-indigo-600 text-white rounded-lg flex items-center justify-center text-sm font-heading font-bold shrink-0">
              {item.step}
            </div>
            <div>
              <div className="font-heading font-semibold text-slate-900 text-sm">{item.title}</div>
              <div className="text-sm text-slate-500 mt-0.5">{item.desc}</div>
            </div>
          </div>
        ))}
      </div>
    ),
  },
];

export const OnboardingWizard = ({ onComplete }) => {
  const [step, setStep] = useState(0);
  const isLast = step === STEPS.length - 1;

  return (
    <div className="min-h-screen bg-[#F8FAFC] flex items-center justify-center p-6" data-testid="onboarding-wizard">
      <div className="w-full max-w-2xl">
        <div className="flex items-center gap-3 mb-8">
          <div className="w-10 h-10 bg-indigo-500 rounded-lg flex items-center justify-center">
            <span className="text-white font-heading font-bold text-lg">U</span>
          </div>
          <span className="font-heading font-bold text-xl tracking-tight text-slate-900">USG</span>
        </div>

        <div className="flex items-center gap-2 mb-8">
          {STEPS.map((_, i) => (
            <div
              key={i}
              className={`h-1.5 rounded-full flex-1 ${i <= step ? "bg-indigo-500" : "bg-slate-200"}`}
              style={{ transition: "background-color 0.3s" }}
            />
          ))}
        </div>

        <div className="bg-white rounded-2xl border border-slate-200 p-8 shadow-sm animate-fade-in" key={step}>
          <div className="mb-6">
            <h2 className="text-2xl font-heading font-bold text-slate-900 tracking-tight">
              {STEPS[step].title}
            </h2>
            <p className="text-slate-500 mt-1">{STEPS[step].subtitle}</p>
          </div>
          {STEPS[step].content}
        </div>

        <div className="flex items-center justify-between mt-6">
          <Button
            variant="ghost"
            data-testid="onboarding-skip-btn"
            onClick={onComplete}
            className="text-slate-500"
          >
            Skip Tutorial
          </Button>
          <div className="flex items-center gap-3">
            {step > 0 && (
              <Button
                variant="outline"
                data-testid="onboarding-back-btn"
                onClick={() => setStep(step - 1)}
              >
                Back
              </Button>
            )}
            <Button
              data-testid="onboarding-next-btn"
              onClick={() => isLast ? onComplete() : setStep(step + 1)}
              className="bg-indigo-600 hover:bg-indigo-700 text-white active:scale-[0.98]"
            >
              {isLast ? (
                <span className="flex items-center gap-2">Get Started <ArrowRight className="w-4 h-4" /></span>
              ) : (
                <span className="flex items-center gap-2">Continue <ArrowRight className="w-4 h-4" /></span>
              )}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default OnboardingWizard;
